import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, Users, Building2, Link2, Box, Loader2,
  Search, ChevronDown, ChevronRight,
  UserPlus, UserMinus, ArrowRightLeft, Edit3, Lock, Unlock,
  EyeOff, Eye, Upload, RefreshCw, Database, CheckCircle2, XCircle,
  FolderPlus, FolderMinus, Briefcase, Trash2, StickyNote, Send,
} from 'lucide-react';
import {
  fetchTenantDetail, fetchTenantAuditLogs, fetchTenantAuditBatch,
  fetchTenantNotes, createTenantNote, deleteTenantNote,
  TenantDetail, AuditLogEntry, AuditLogParams, TenantNote,
} from '../services/api';
import { FunnelStrip } from './DashboardPage';
import ApiKeysSection from './ApiKeysSection';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACTION_ICONS: Record<string, React.ReactNode> = {
  employee_created: <UserPlus size={14} />,
  employee_updated: <Edit3 size={14} />,
  employee_moved: <ArrowRightLeft size={14} />,
  employee_deleted: <Trash2 size={14} />,
  employee_hidden: <EyeOff size={14} />,
  employee_unhidden: <Eye size={14} />,
  supervisor_locked: <Lock size={14} />,
  supervisor_unlocked: <Unlock size={14} />,
  supervisor_change_accepted: <CheckCircle2 size={14} />,
  supervisor_change_dismissed: <XCircle size={14} />,
  department_created: <FolderPlus size={14} />,
  department_updated: <Edit3 size={14} />,
  department_deleted: <FolderMinus size={14} />,
  open_role_created: <Briefcase size={14} />,
  open_role_deleted: <UserMinus size={14} />,
  import_completed: <Upload size={14} />,
  sync_completed: <RefreshCw size={14} />,
  backup_restored: <Database size={14} />,
};

const CATEGORY_COLORS: Record<string, string> = {
  manual: 'bg-blue-100 text-blue-700',
  import: 'bg-purple-100 text-purple-700',
  sync: 'bg-green-100 text-green-700',
  system: 'bg-gray-100 text-gray-700',
};

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-amber-100 text-amber-800',
  deleted: 'bg-red-100 text-red-800',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

const ROLE_COLORS: Record<string, string> = {
  owner: 'bg-purple-100 text-purple-800',
  admin: 'bg-blue-100 text-blue-800',
  planner: 'bg-amber-100 text-amber-800',
  user: 'bg-gray-100 text-gray-700',
};

// ---------------------------------------------------------------------------
// Batch children component
// ---------------------------------------------------------------------------

function BatchChildren({ tenantId, batchId }: { tenantId: string; batchId: string }) {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadBatch = useCallback(async (cursor?: string) => {
    setLoading(true);
    try {
      const data = await fetchTenantAuditBatch(tenantId, batchId, 50, cursor);
      setEntries(prev => cursor ? [...prev, ...data.entries] : data.entries);
      setNextCursor(data.nextCursor);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [tenantId, batchId]);

  useEffect(() => { loadBatch(); }, [loadBatch]);

  if (loading && entries.length === 0) {
    return (
      <div className="flex items-center gap-2 p-3 pl-10 text-sm text-gray-500">
        <Loader2 size={14} className="animate-spin" />
        Loading changes...
      </div>
    );
  }

  return (
    <div className="border-l-2 border-gray-200 ml-4">
      {entries.map((entry) => (
        <EntryRow key={entry.id} entry={entry} tenantId={tenantId} indent />
      ))}
      {nextCursor && (
        <button
          onClick={() => loadBatch(nextCursor)}
          className="ml-10 px-3 py-1 text-xs text-blue-600 hover:text-blue-800"
        >
          Load more changes...
        </button>
      )}
      {entries.length === 0 && !loading && (
        <div className="pl-10 py-2 text-sm text-gray-400">No individual changes recorded</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single audit entry row
// ---------------------------------------------------------------------------

function EntryRow({ entry, tenantId, indent = false }: { entry: AuditLogEntry; tenantId: string; indent?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const isBatchHeader = entry.action === 'import_completed' || entry.action === 'sync_completed' || entry.action === 'backup_restored';
  const icon = ACTION_ICONS[entry.action] || <Edit3 size={14} />;
  const catClass = CATEGORY_COLORS[entry.category] || CATEGORY_COLORS.system;

  return (
    <div>
      <div
        className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition ${indent ? 'pl-10' : ''} ${isBatchHeader ? 'cursor-pointer' : ''}`}
        onClick={isBatchHeader ? () => setExpanded(!expanded) : undefined}
      >
        <div className="flex-shrink-0 mt-0.5 w-4">
          {isBatchHeader && (
            expanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />
          )}
        </div>
        <div className="flex-shrink-0 mt-0.5 text-gray-500">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${catClass}`}>
              {entry.category}
            </span>
            <span className="text-sm text-gray-900">{entry.description}</span>
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
            {entry.timestamp && <span>{formatTimestamp(entry.timestamp)}</span>}
            {entry.actorEmail && <span>by {entry.actorEmail}</span>}
          </div>
        </div>
        {entry.hasChanges === false && (
          <span className="flex-shrink-0 px-2 py-0.5 text-[10px] bg-gray-100 text-gray-500 rounded">
            no changes
          </span>
        )}
      </div>
      {isBatchHeader && expanded && entry.batchId && (
        <BatchChildren tenantId={tenantId} batchId={entry.batchId} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notes section
// ---------------------------------------------------------------------------

const NotesSection: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const [notes, setNotes] = useState<TenantNote[]>([]);
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    try {
      setNotes(await fetchTenantNotes(tenantId));
    } catch { /* ignore */ }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    setPosting(true);
    try {
      await createTenantNote(tenantId, trimmed);
      setBody('');
      await load();
    } catch { /* ignore */ }
    finally { setPosting(false); }
  };

  const handleDelete = async (noteId: string) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await deleteTenantNote(tenantId, noteId);
      await load();
    } catch { /* ignore */ }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <StickyNote size={16} /> Notes
      </h2>
      <div className="flex gap-2 mb-3">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit();
          }}
          placeholder="Add a note (Cmd/Ctrl+Enter to save)..."
          rows={2}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <button
          onClick={handleSubmit}
          disabled={posting || !body.trim()}
          className="self-start flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <Send size={14} />
          {posting ? 'Saving...' : 'Save'}
        </button>
      </div>
      {notes.length === 0 ? (
        <div className="text-xs text-gray-400 py-2">No notes yet</div>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => (
            <div key={n.id} className="border border-gray-200 rounded-lg p-3 group">
              <div className="text-sm text-gray-900 whitespace-pre-wrap">{n.body}</div>
              <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                <span>{n.author} • {formatTimestamp(n.createdAt)}</span>
                <button
                  onClick={() => handleDelete(n.id)}
                  className="opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-800 transition"
                  title="Delete note"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main TenantDetailPage
// ---------------------------------------------------------------------------

interface Props {
  tenantId: string;
  onBack: () => void;
}

export const TenantDetailPage: React.FC<Props> = ({ tenantId, onBack }) => {
  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Audit log state
  const [auditEntries, setAuditEntries] = useState<AuditLogEntry[]>([]);
  const [auditCursor, setAuditCursor] = useState<string | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState('');
  const [onlyChanges, setOnlyChanges] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Load tenant detail
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await fetchTenantDetail(tenantId);
        setTenant(data);
      } catch {
        setError('Failed to load tenant details');
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId]);

  // Search debounce
  const handleSearchChange = useCallback((val: string) => {
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(val), 300);
  }, []);

  // Load audit logs
  const loadAuditLogs = useCallback(async (cursor?: string) => {
    setAuditLoading(true);
    try {
      const params: AuditLogParams = { limit: 50 };
      if (cursor) params.cursor = cursor;
      if (debouncedSearch) params.search = debouncedSearch;
      if (category) params.category = category;
      if (onlyChanges) params.hasChanges = true;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate + 'T23:59:59';

      const data = await fetchTenantAuditLogs(tenantId, params);
      setAuditEntries(prev => cursor ? [...prev, ...data.entries] : data.entries);
      setAuditCursor(data.nextCursor);
    } catch {
      // silently fail
    } finally {
      setAuditLoading(false);
    }
  }, [tenantId, debouncedSearch, category, onlyChanges, startDate, endDate]);

  // Reload audit logs when filters change
  useEffect(() => {
    setAuditEntries([]);
    setAuditCursor(null);
    loadAuditLogs();
  }, [loadAuditLogs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-6">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft size={16} /> Back to tenants
        </button>
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error || 'Tenant not found'}</div>
      </div>
    );
  }

  const userCount = tenant.users?.length ?? tenant.usage?.users ?? 0;
  const employeeCount = tenant.usage?.employees ?? 0;

  const infoCards = [
    { label: 'Users', value: userCount, icon: <Users size={20} />, color: 'bg-blue-50 text-blue-600' },
    { label: 'Employees', value: employeeCount, icon: <Building2 size={20} />, color: 'bg-green-50 text-green-600' },
    { label: 'Connections', value: tenant.connectionCount, icon: <Link2 size={20} />, color: 'bg-purple-50 text-purple-600' },
    { label: 'Planners', value: tenant.sandboxCount, icon: <Box size={20} />, color: 'bg-amber-50 text-amber-600' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-3">
            <ArrowLeft size={16} /> Back to tenants
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[tenant.status] || 'bg-gray-100 text-gray-800'}`}>
              {tenant.status}
            </span>
            <span className="text-sm text-gray-500 font-mono">/{tenant.slug}</span>
          </div>
          {tenant.ownerEmail && (
            <p className="text-sm text-gray-500 mt-1">Owner: {tenant.ownerEmail}</p>
          )}
          {tenant.termsAcceptedAt ? (
            <p className="text-sm text-gray-500 mt-1">
              Terms accepted: v{tenant.termsVersion} by {tenant.termsAcceptedBy} on {new Date(tenant.termsAcceptedAt).toLocaleDateString()}
            </p>
          ) : (
            <p className="text-sm text-amber-600 mt-1">Terms not yet accepted</p>
          )}
          <div className="text-sm mt-2 flex items-center gap-2 flex-wrap">
            <span className="text-gray-500">Access:</span>
            {tenant.accessMode === 'domain' && (tenant.allowedDomains?.length ?? 0) > 0 ? (
              <>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                  🌐 Anyone from these domains
                </span>
                {tenant.allowedDomains?.map((d) => (
                  <span key={d} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-mono">
                    {d}
                  </span>
                ))}
              </>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                🔒 Invite-only
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Info cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {infoCards.map((card) => (
            <div key={card.label} className="bg-white rounded-lg shadow-sm p-4 flex items-center gap-3">
              <div className={`p-2 rounded-lg ${card.color}`}>{card.icon}</div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{card.value}</div>
                <div className="text-xs text-gray-500">{card.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Onboarding funnel */}
        {tenant.funnel && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700">Onboarding</h2>
              {tenant.activity?.lastActionAt && (
                <span className="text-xs text-gray-500">
                  Last action {formatTimestamp(tenant.activity.lastActionAt)} • {tenant.activity.activeUsers7d ?? 0} active 7d / {tenant.activity.activeUsers30d ?? 0} 30d
                </span>
              )}
            </div>
            <FunnelStrip funnel={tenant.funnel} size="md" />
          </div>
        )}

        {/* Notes */}
        <NotesSection tenantId={tenantId} />

        {/* Public API keys */}
        <ApiKeysSection tenantId={tenantId} tenantName={tenant.name} />

        {/* Users table */}
        {tenant.users && tenant.users.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-700">Users</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last login</th>
                    <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Edits 30d</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invited by</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tenant.users.map((u) => (
                    <tr key={u.email} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 text-sm text-gray-900">{u.email}</td>
                      <td className="px-4 py-2.5 text-sm text-gray-500">{u.name || '-'}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[u.role || 'user'] || ROLE_COLORS.user}`}>
                          {u.role || 'user'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-gray-500" title={u.lastLoginAt || ''}>
                        {u.lastLoginAt ? formatTimestamp(u.lastLoginAt) : '-'}
                      </td>
                      <td className={`px-4 py-2.5 text-sm text-right ${(u.edits30d ?? 0) > 0 ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                        {u.edits30d ?? 0}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-gray-500">{u.invitedBy || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Audit Log */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-700">Audit Log</h2>

          {/* Filter bar */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search by name, email, or department..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-2 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <span className="text-gray-400 text-sm">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-2 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
              >
                <option value="">All categories</option>
                <option value="manual">Manual</option>
                <option value="import">Import</option>
                <option value="sync">Sync</option>
                <option value="system">System</option>
              </select>
              <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={onlyChanges}
                  onChange={(e) => setOnlyChanges(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600"
                />
                Only changes
              </label>
            </div>
          </div>

          {/* Entries */}
          <div className="bg-white rounded-lg shadow-sm divide-y">
            {auditLoading && auditEntries.length === 0 ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="animate-spin text-gray-400" size={24} />
              </div>
            ) : auditEntries.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <RefreshCw size={48} className="mx-auto mb-2 text-gray-300" />
                <p>No audit log entries found</p>
              </div>
            ) : (
              auditEntries.map((entry) => (
                <EntryRow key={entry.id} entry={entry} tenantId={tenantId} />
              ))
            )}
          </div>

          {/* Load more */}
          {auditCursor && (
            <div className="flex justify-center">
              <button
                onClick={() => loadAuditLogs(auditCursor)}
                disabled={auditLoading}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                {auditLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load more'
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantDetailPage;
